import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Loader, Popup } from "semantic-ui-react";

import { DATE_FORMATS_FNS } from "components/DatePicker";
import { DBBAppointmentDateModal } from "components/DBB/components";
import "components/DBB/DBB.scss";
import { useDBBPortalQuery, useDBBRefreshQuery, useDBBTyreLimits } from "components/DBB/hooks";
import { DBB_STATUSES } from "models";
import { formatDate, isDateAfter } from "utils/dateHelperFunctions";

import type { Appointment } from "models";
import type { MouseEvent } from "react";

export enum DBB_STATE {
  InOrder = "O",
  InStock = "S",
  FromStock = "G",
  ToStorage = "T",
  FittedOnCar = "C",
  FromLocator = "R",
  Deleted = "D"
}

type DBBProps = {
  appointment: Appointment;
};

export const DBB = ({ appointment }: DBBProps) => {
  const { t } = useTranslation();

  const [isDBBAppointmentDateModalOpen, setIsDBBAppointmentDateModalOpen] = useState(false);

  const { openDBBPortal } = useDBBPortalQuery(appointment.id);
  const { refreshDBB } = useDBBRefreshQuery(appointment.id);
  const { tyreLimits, hasStorageTyreBelowNecessaryTreadDepth, isFetchingTyreLimits } = useDBBTyreLimits(appointment);

  const canCreateLocally =
    appointment.car?.has_dbb &&
    !appointment.car.dbb_appointment_date &&
    !appointment.car.dbb_tyre_pickup_requested_at &&
    tyreLimits &&
    !hasStorageTyreBelowNecessaryTreadDepth;

  const handleRefreshDBB = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    refreshDBB();
  };

  const handleDBBButtonMouseDown = () => {
    if (canCreateLocally) return setIsDBBAppointmentDateModalOpen(true);
    openDBBPortal();
  };

  const hasFinalCarCheckDBBStatus = appointment.final_car_check_dbb_status !== DBB_STATUSES.NoDBBStatus;
  const dbbStatus = hasFinalCarCheckDBBStatus ? appointment.final_car_check_dbb_status : appointment.car_check_dbb_status;
  const dbbStatusReason = hasFinalCarCheckDBBStatus ? appointment.final_car_check_dbb_status_reason : appointment.car_check_dbb_status_reason;

  let status = "no-dbb";
  let statusColor = "white";
  let icon = false;
  let iconColor = "red";
  let popupText = null;

  if (appointment.car?.has_dbb) {
    if (appointment.car.dbb_appointment_date) {
      if (isDateAfter(new Date(), appointment.car.dbb_appointment_date)) {
        status = "valid-appointment";
        statusColor = "green";
      } else {
        status = "waiting-appointment";
        statusColor = "orange";
      }

      if (dbbStatus !== DBB_STATUSES.NoDBBStatus) {
        icon = true;
        if (dbbStatus === DBB_STATUSES.DBBPushed) {
          iconColor = "green";
        } else if (dbbStatus === DBB_STATUSES.DBBIsPending) {
          iconColor = "orange";
          popupText = {
            bold: dbbStatusReason || t("v8_miniportal_approval", "Miniportal is pending your approval,"),
            normal: t("v8_for_issues_reach_dbb_support", "for issues please reach out to DBB Support.")
          };
        } else {
          iconColor = "red";
          popupText = {
            bold: dbbStatusReason || t("v8_unexpected_error", "An unexpected error has occurred,"),
            normal: t("v8_for_issues_reach_us", "for issues please reach out to us.")
          };
        }
      }
    } else {
      status = "no-appointment";
      statusColor = "red";

      if (hasStorageTyreBelowNecessaryTreadDepth) {
        popupText = {
          bold: t("v8_use_miniportal_to_adjust_tyres", "Use the miniportal to adjust the tyres that are delivered"),
          normal: appointment.car.dbb_works_with_free_stock
            ? t("v8_free_stock_must_be_checked_and_tyres_released", "Free stock must be checked and the tyres must be released to delivered in the miniportal")
            : ""
        };
      }
    }

    if (appointment.dbb_state === DBB_STATE.ToStorage) {
      statusColor = "purple";
    }
  }

  return (
    <>
      <Popup
        hoverable
        wide
        disabled={!popupText}
        className={`DBBbutton ${icon ? iconColor : statusColor}`}
        content={
          popupText ? (
            <div className="popup-content">
              <span>{popupText.bold}</span> {popupText.normal}
            </div>
          ) : null
        }
        trigger={
          <Button className={`DBB ${statusColor} -appointment-status`} size="small" onMouseDown={handleDBBButtonMouseDown}>
            {icon && (
              <div className={`checked-tire ${iconColor}`}>
                <Icon className="tire" color={iconColor} />
                <Icon className="circle check" color={iconColor} />
              </div>
            )}
            {status !== "no-dbb" && status !== "no-appointment" && (
              <div className="appointment-date">
                {appointment.car?.dbb_appointment_date && formatDate(appointment.car.dbb_appointment_date, DATE_FORMATS_FNS.dateMonthYear)}
              </div>
            )}
            <strong className="button-label">DBB</strong>
            {isFetchingTyreLimits && <Loader active inline size="tiny" />}
            {!isFetchingTyreLimits && <Icon className={canCreateLocally ? "plus" : "arrow up right from square"} color="green" />}
            <div className="secondary-button" onMouseDown={handleRefreshDBB}>
              <Icon className="arrow rotate" color="black" />
            </div>
          </Button>
        }
      />
      <DBBAppointmentDateModal appointment={appointment} open={isDBBAppointmentDateModalOpen} onClose={() => setIsDBBAppointmentDateModalOpen(false)} />
    </>
  );
};
